import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  CreditCard, CalendarClock, Check, Loader2, X, CalendarDays, Wallet, MessageSquare, ShieldCheck,
} from "lucide-react";
import { CardSkeleton } from "@/components/Skeleton";

export default function PatientMembership() {
  const [renewal, setRenewal] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null); // {type:'success'|'error', text}
  const [modal, setModal] = useState(null); // 'date' | 'payment' | 'contact'

  const load = useCallback(() => {
    api.get("/patient/renewal").then((r) => setRenewal(r.data)).catch(() => {});
    api.get("/patient/membership-summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // Poll Stripe checkout status after redirect back
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    setResult({ type: "pending", text: "Confirming your payment…" });
    const poll = async () => {
      if (attempts >= 7) { setResult({ type: "error", text: "Still processing — please check back shortly." }); return; }
      attempts += 1;
      try {
        const { data } = await api.get(`/patient/renewal/checkout-status/${sid}`);
        if (data.payment_status === "paid") {
          setResult({ type: "success", text: "Payment successful — your membership is renewed!" });
          window.history.replaceState({}, "", "/portal/membership");
          load();
          return;
        }
        if (data.status === "expired") {
          setResult({ type: "error", text: "Payment session expired. Please try again." });
          window.history.replaceState({}, "", "/portal/membership");
          return;
        }
      } catch (e) { /* keep polling */ }
      setTimeout(poll, 2000);
    };
    poll();
  }, [load]);

  const startCheckout = async () => {
    setBusy("pay"); setResult(null);
    try {
      const { data } = await api.post("/patient/renewal/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setResult({ type: "error", text: formatApiError(e.response?.data?.detail) || e.message });
      setBusy("");
    }
  };

  const act = async (key, url, body) => {
    setBusy(key); setResult(null);
    try {
      const { data } = await api.post(url, body || {});
      setResult({ type: "success", text: data.message });
      setModal(null);
      load();
    } catch (e) {
      setResult({ type: "error", text: formatApiError(e.response?.data?.detail) || e.message });
    }
    setBusy("");
  };

  if (!renewal) return <CardSkeleton lines={3} />;

  const overdue = renewal.days_until != null && renewal.days_until < 0;
  const headline =
    overdue ? `Your membership renewal is ${Math.abs(renewal.days_until)} days overdue`
      : renewal.days_until === 0 ? "Your membership renews today"
      : `Your membership renews in ${renewal.days_until} days`;

  return (
    <div className="space-y-6" data-testid="patient-membership">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">Membership</h1>
        <p className="text-sm text-muted-foreground mt-1">You're always in control. Choose what works best for you.</p>
      </div>

      {/* Renewal summary */}
      {summary && (
        <div className="bg-card border border-border rounded-xl p-5" data-testid="membership-plain">
          <p className="text-sm font-medium">What your membership covers</p>
          <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
            {(summary.includes || []).map((item) => (
              <li key={item} className="inline-flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> {item} care and check-ins
              </li>
            ))}
            <li className="inline-flex items-center gap-2">
              <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> Messaging with your care team
            </li>
            <li className="inline-flex items-center gap-2">
              <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> Lab review and follow-up
            </li>
          </ul>
          {summary.billed_every !== "month" && summary.monthly_equivalent != null && (
            <p className="text-xs text-muted-foreground mt-3">
              Billed every {summary.billed_every} — about ${summary.monthly_equivalent} a month.
            </p>
          )}
        </div>
      )}

      <div className={`rounded-xl border p-6 ${overdue ? "bg-amber-50 border-amber-200" : "bg-card border-border"}`} data-testid="renewal-summary">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">{renewal.membership_plan}</p>
            <h2 className="font-heading text-xl font-bold mt-1">{headline}</h2>
            {/* Billing-period honest: an annual plan reads "$299 a year", never a bare
                "$299" sitting next to monthly-looking figures. */}
            <p className="text-sm text-muted-foreground mt-1" data-testid="membership-price">
              {summary?.price_label || `$${renewal.price}`} · next payment {renewal.renewal_date}
            </p>
            <p className="text-xs text-muted-foreground mt-2 inline-flex items-center gap-1">
              <Wallet className="w-3.5 h-3.5" /> {renewal.payment_method?.brand} ending {renewal.payment_method?.last4}
            </p>
          </div>
          <CalendarClock className={`w-7 h-7 ${overdue ? "text-amber-600" : "text-muted-foreground"}`} />
        </div>
      </div>

      {result && (
        <p data-testid="renewal-result" className={`text-sm rounded-md px-3 py-2 border ${result.type === "success" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : result.type === "pending" ? "text-sky-700 bg-sky-50 border-sky-200" : "text-red-600 bg-red-50 border-red-200"}`}>
          {result.text}
        </p>
      )}

      {/* Options */}
      <div className="grid sm:grid-cols-2 gap-3">
        <OptionButton
          primary icon={Check} title="Pay Now"
          subtitle={`Renew today for $${renewal.price}`}
          busy={busy === "pay"} testid="option-pay-now"
          onClick={startCheckout}
        />
        <OptionButton
          icon={CalendarClock} title="Keep My Scheduled Renewal"
          subtitle={`Auto-renews ${renewal.renewal_date}`}
          busy={busy === "keep"} testid="option-keep"
          onClick={() => act("keep", "/patient/renewal/keep")}
        />
        {renewal.allow_date_change && (
          <OptionButton
            icon={CalendarDays} title="Request a Different Date"
            subtitle="Choose a date that works for you"
            testid="option-request-date"
            onClick={() => setModal("date")}
          />
        )}
        <OptionButton
          icon={Wallet} title="Update Payment Method"
          subtitle="Change your card on file"
          testid="option-payment-method"
          onClick={() => setModal("payment")}
        />
        <OptionButton
          icon={MessageSquare} title="Contact the Clinic"
          subtitle="Have a question? Reach out"
          testid="option-contact"
          onClick={() => setModal("contact")}
        />
      </div>

      <p className="text-xs text-muted-foreground flex items-center gap-1.5 justify-center pt-2">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
        No surprises. You'll never be charged without choosing an option above.
      </p>

      {modal === "date" && <RequestDateModal busy={busy === "date"} onClose={() => setModal(null)} onSubmit={(d, note) => act("date", "/patient/renewal/request-date", { requested_date: d, note })} />}
      {modal === "payment" && <PaymentModal busy={busy === "payment"} onClose={() => setModal(null)} onSubmit={(brand, last4) => act("payment", "/patient/renewal/payment-method", { brand, last4 })} />}
      {modal === "contact" && <ContactModal busy={busy === "contact"} onClose={() => setModal(null)} onSubmit={(message) => act("contact", "/patient/renewal/contact", { message })} />}
    </div>
  );
}

function OptionButton({ icon: Icon, title, subtitle, onClick, primary, busy, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={busy}
      className={`text-left rounded-xl border p-4 flex items-start gap-3 transition-all hover:shadow-sm hover:-translate-y-[1px] disabled:opacity-60 ${primary ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${primary ? "bg-white/15" : "bg-secondary"}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className={`w-[18px] h-[18px] ${primary ? "" : "text-primary"}`} />}
      </div>
      <div>
        <p className="font-semibold text-sm">{title}</p>
        <p className={`text-xs mt-0.5 ${primary ? "text-white/70" : "text-muted-foreground"}`}>{subtitle}</p>
      </div>
    </button>
  );
}

function Shell({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid={testid}>
      <div className="bg-card rounded-xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-heading text-base font-semibold">{title}</h3>
          <button onClick={onClose} data-testid="renewal-modal-close" className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-input bg-card px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring";

function RequestDateModal({ onClose, onSubmit, busy }) {
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  return (
    <Shell title="Request a Different Date" onClose={onClose} testid="modal-request-date">
      <div className="space-y-3">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} data-testid="request-date-input" />
        <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note (optional)" rows={2} className={inputCls} data-testid="request-date-note" />
        <button disabled={!date || busy} onClick={() => onSubmit(date, note)} data-testid="submit-request-date" className="w-full bg-primary text-primary-foreground rounded-md py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 flex items-center justify-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send request
        </button>
      </div>
    </Shell>
  );
}

function PaymentModal({ onClose, onSubmit, busy }) {
  const [brand, setBrand] = useState("Visa");
  const [last4, setLast4] = useState("");
  return (
    <Shell title="Update Payment Method" onClose={onClose} testid="modal-payment">
      <div className="space-y-3">
        <select value={brand} onChange={(e) => setBrand(e.target.value)} className={inputCls} data-testid="payment-brand">
          <option>Visa</option><option>Mastercard</option><option>Amex</option><option>Discover</option>
        </select>
        <input value={last4} onChange={(e) => setLast4(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="Last 4 digits" className={inputCls} data-testid="payment-last4" />
        <p className="text-[11px] text-muted-foreground">Demo only — no real card is stored or charged.</p>
        <button disabled={last4.length !== 4 || busy} onClick={() => onSubmit(brand, last4)} data-testid="submit-payment" className="w-full bg-primary text-primary-foreground rounded-md py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 flex items-center justify-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save card
        </button>
      </div>
    </Shell>
  );
}

function ContactModal({ onClose, onSubmit, busy }) {
  const [message, setMessage] = useState("");
  return (
    <Shell title="Contact the Clinic" onClose={onClose} testid="modal-contact">
      <div className="space-y-3">
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="How can we help?" rows={4} className={inputCls} data-testid="contact-message" />
        <button disabled={!message.trim() || busy} onClick={() => onSubmit(message)} data-testid="submit-contact" className="w-full bg-primary text-primary-foreground rounded-md py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 flex items-center justify-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send message
        </button>
      </div>
    </Shell>
  );
}
